import { t, useLanguage } from "@/lib/i18n";
import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function MediaAdmin({ embedded = false }) {
  useLanguage();
  const { user } = useAuth();
  const [limits, setLimits] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (user?.role === "admin") api.get("/settings/media").then(({ data }) => setLimits(data))
      .catch(() => setMessage("Could not load settings. Please reload."));
  }, [user?.role]);
  if (user?.role !== "admin") return <Navigate to="/app" replace />;
  const save = async (event) => {
    event.preventDefault(); setSaving(true); setMessage("");
    try {
      const { data } = await api.put("/settings/media", limits);
      setLimits(data); setMessage("Upload limits saved.");
    } catch (e) { setMessage(formatApiErrorDetail(e.response?.data?.detail) || "Could not save limits."); }
    finally { setSaving(false); }
  };
  const Container = embedded ? "section" : "main";
  const Heading = embedded ? "h2" : "h1";
  return <Container className={embedded ? "" : "min-h-screen bg-[#FAF7F2] p-4 sm:p-6"}>
    <div className="max-w-xl mx-auto space-y-6">
      {!embedded && <Link to="/app" className="text-amber-800 underline">{t("Back to tree")}</Link>}
      <Heading className="font-serif text-3xl">{t("Photo and video upload limits")}</Heading>
      <p>{t("Set photo and video counts per member and the maximum size of each file separately. The photo count must be at least 1. A video count of 0 hides video uploads. File sizes allow decimals and 0 MB, up to 1024 MB. Existing uploads are kept.")}</p>
      <p>{t("Storage is shared by all audio, photos and videos in an account. Active subscriptions use their plan's storage allowance. Existing media is kept when limits are lowered.")}</p>
      {message && <p role="status">{t(message)}</p>}
      {limits && <form onSubmit={save} className="space-y-4 bg-white border rounded-xl p-5">
        {[["max_photos", "Maximum free photos per member", 1], ["max_photo_mb", "Maximum photo size (MB)"], ["max_videos", "Maximum videos per member", 0], ["max_video_mb", "Maximum video size (MB)"]].map(([key, label, min]) => <div key={key}>
          <Label htmlFor={key}>{t(label)}</Label>
          <Input id={key} type="number" min={min} max="1024" step={min === undefined ? "any" : "1"} required value={limits[key]} onChange={(e) => setLimits({ ...limits, [key]: e.target.value === "" ? "" : Number(e.target.value) })} />
        </div>)}
        <div><Label htmlFor="max_storage_mb">{t("Max Storage limit (MB)")}</Label><Input id="max_storage_mb" type="number" min="1" max="1048576" step="1" required value={limits.max_storage_mb ?? 100} onChange={event => setLimits({ ...limits, max_storage_mb: event.target.value === "" ? "" : Number(event.target.value) })} /></div>
        <div><Label htmlFor="max_albums">{t("Maximum private albums per account")}</Label><Input id="max_albums" type="number" min="0" max="1024" step="1" required value={limits.max_albums ?? 5} onChange={(e) => setLimits({ ...limits, max_albums: e.target.value === "" ? "" : Number(e.target.value) })} /></div>
        <Button type="submit" disabled={saving}>{saving ? t("Saving...") : t("Save limits")}</Button>
      </form>}
    </div>
  </Container>;
}
